<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SettingsLayout from 'dashboard/components-next/Settings/SettingsLayout.vue';
import IntegrationEmptyState from '../shared/IntegrationEmptyState.vue';
import IntegrationConnectionCard from '../shared/IntegrationConnectionCard.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import MetaConversionsAPI from 'dashboard/api/metaConversions';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const uiFlags = computed(
  () => getters['metaConversions/getUIFlags'].value || {}
);
const integrations = computed(
  () => getters['metaConversions/getMetaConversions'].value || []
);

const isLoading = computed(
  () => uiFlags.value.isFetching || uiFlags.value.isCreating
);
const isEmpty = computed(() => integrations.value.length === 0);
const isConnected = computed(() => integrations.value.length > 0);
const integration = computed(() => integrations.value[0] || null);

const pixelId = ref('');
const accessToken = ref('');
const testEventCode = ref('');

onMounted(async () => {
  await store.dispatch('metaConversions/get');
  pixelId.value = integration.value?.pixel_id || '';
  testEventCode.value = integration.value?.settings?.test_event_code || '';
});

const handleConnect = async () => {
  try {
    await store.dispatch('metaConversions/authorize');
  } catch (error) {
    useAlert(
      error?.message || t('INTEGRATION_SETTINGS.META_CONVERSIONS.CONNECT_ERROR')
    );
  }
};

const handleDisconnect = async () => {
  // eslint-disable-next-line no-alert
  if (
    window.confirm(t('INTEGRATION_SETTINGS.META_CONVERSIONS.DISCONNECT.CONFIRM'))
  ) {
    try {
      await store.dispatch('metaConversions/delete', integration.value.id);
      useAlert(t('INTEGRATION_SETTINGS.META_CONVERSIONS.DISCONNECT.SUCCESS'));
    } catch (error) {
      useAlert(
        error?.message ||
          t('INTEGRATION_SETTINGS.META_CONVERSIONS.DISCONNECT.ERROR')
      );
    }
  }
};

const handleSaveSettings = async () => {
  try {
    const payload = {
      id: integration.value.id,
      pixel_id: pixelId.value,
      test_event_code: testEventCode.value,
    };
    if (accessToken.value) {
      payload.access_token = accessToken.value;
    }
    await store.dispatch('metaConversions/update', payload);
    accessToken.value = '';
    useAlert(t('INTEGRATION_SETTINGS.META_CONVERSIONS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.message || t('INTEGRATION_SETTINGS.META_CONVERSIONS.SAVE_ERROR')
    );
  }
};

const handleManualConnect = async () => {
  if (!accessToken.value) {
    useAlert(t('INTEGRATION_SETTINGS.META_CONVERSIONS.TOKEN_REQUIRED'));
    return;
  }
  try {
    await MetaConversionsAPI.create({
      access_token: accessToken.value,
      pixel_id: pixelId.value,
      test_event_code: testEventCode.value,
    });
    accessToken.value = '';
    await store.dispatch('metaConversions/get');
    useAlert(t('INTEGRATION_SETTINGS.META_CONVERSIONS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.message || t('INTEGRATION_SETTINGS.META_CONVERSIONS.CONNECT_ERROR')
    );
  }
};
</script>

<template>
  <SettingsLayout
    :header-title="$t('INTEGRATION_SETTINGS.META_CONVERSIONS.HEADER')"
    :button-label="
      isConnected
        ? $t('INTEGRATION_SETTINGS.META_CONVERSIONS.DISCONNECT_BUTTON')
        : $t('INTEGRATION_SETTINGS.META_CONVERSIONS.CONNECT_BUTTON')
    "
    @click="isConnected ? handleDisconnect() : handleConnect()"
  >
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>

    <div v-else-if="isEmpty" class="space-y-6">
      <IntegrationEmptyState
        icon="i-lucide-megaphone"
        :title="$t('INTEGRATION_SETTINGS.META_CONVERSIONS.EMPTY_STATE')"
        :description="$t('INTEGRATION_SETTINGS.META_CONVERSIONS.DESCRIPTION')"
        :button-label="$t('INTEGRATION_SETTINGS.META_CONVERSIONS.CONNECT_BUTTON')"
        @action="handleConnect"
      />
      <div class="mx-auto max-w-md space-y-3 rounded-lg border border-n-weak p-4">
        <p class="text-sm text-n-slate-11">
          {{ $t('INTEGRATION_SETTINGS.META_CONVERSIONS.MANUAL_HELP') }}
        </p>
        <Input
          v-model="accessToken"
          :label="$t('INTEGRATION_SETTINGS.META_CONVERSIONS.ACCESS_TOKEN')"
          type="password"
        />
        <Input
          v-model="pixelId"
          :label="$t('INTEGRATION_SETTINGS.META_CONVERSIONS.PIXEL_ID')"
        />
        <Button
          :label="$t('INTEGRATION_SETTINGS.META_CONVERSIONS.MANUAL_CONNECT')"
          @click="handleManualConnect"
        />
      </div>
    </div>

    <div v-else class="space-y-4">
      <IntegrationConnectionCard
        icon="i-lucide-megaphone"
        icon-class="bg-gradient-to-br from-blue-500 to-blue-700"
        :title="
          integration?.account_name ||
          integration?.settings?.account_name ||
          $t('INTEGRATION_SETTINGS.META_CONVERSIONS.CONNECTED')
        "
        :description="
          $t('INTEGRATION_SETTINGS.META_CONVERSIONS.CONNECTED_DESCRIPTION')
        "
      />
      <div class="space-y-3 rounded-lg border border-n-weak p-4">
        <Input
          v-model="pixelId"
          :label="$t('INTEGRATION_SETTINGS.META_CONVERSIONS.PIXEL_ID')"
        />
        <Input
          v-model="testEventCode"
          :label="$t('INTEGRATION_SETTINGS.META_CONVERSIONS.TEST_EVENT_CODE')"
        />
        <Input
          v-model="accessToken"
          :label="$t('INTEGRATION_SETTINGS.META_CONVERSIONS.ACCESS_TOKEN_UPDATE')"
          type="password"
        />
        <Button
          :label="$t('INTEGRATION_SETTINGS.META_CONVERSIONS.SAVE_SETTINGS')"
          @click="handleSaveSettings"
        />
      </div>
    </div>
  </SettingsLayout>
</template>
